<template lang='pug'>
  .product-card(
    v-if='currentVariant && currentVariant.attributes'
    @click="notifyDataLayer"
    @mouseenter='mouseoverProductCard'
    @mouseleave='mouseleaveProductCard'
    :data-carousel-id='gaOptions ? gaOptions.carouselId : undefined'
    :data-product-id='product.id'
    :data-product-name='product.attributes.name'
    :data-product-brand='currentVariant.attributes["brand-name"]'
    :data-variant-id='activeVariant(product).id'
  )
    product-promo(v-if='size === "lg"' :short='true' :ad='productAd' :option-type='productOptionTypeText')
    router-link.product-card-image-container(:class='gaClasses' :to='productsShowRoute' @click.native='handleProductClick')
      template(v-if='lazy')
        img.product-card-image(v-lazy='variantImage' :key='variantImage' :alt='product.attributes.name')
      template(v-else)
        img.product-card-image(:src='variantImage' :alt='product.attributes.name')
      .out-of-stock(v-if='outOfStock' v-text='$t("products.outOfStock")')
      span.product-card-early-access(v-if='earlyAccessBadge' :style="{ opacity: outOfStock ? '50%' : '100%' }")
        | {{ earlyAccessBadge }}
    product-heart(
      v-if='size === "lg" && showHeart'
      :variant='currentVariant'
      :product='product'
    )

    .product-card-actions(:class='showProductActions ? "visible" : "invisible"' v-if='currentVariant && hasShownProductActions')
      ProductCallToAction(
        :product='product'
        :variant='currentVariant'
        :gaOptions='gaOptions'
        size='sm'
        :showRemindMe='showRemindMeExtended'
        :isAppExclusive='isAppExclusive'
        :earlyAccessNotificationEventDetails='earlyAccessNotificationEventDetails'
      )

    router-link.product-card-description(:class='gaClasses' :to='productsShowRoute' @click.native='handleProductClick')
      p.product-card-label(v-if='label')
        span.product-card-label-primary(v-text='label')
      p.product-card-brand(v-text='currentVariant.attributes["brand-name"]')
      p.product-card-product(v-text='product.attributes.name')
      product-price(:variant='currentVariant')
      .rating-container(v-if='product.attributes.rating > 0')
        product-rating-stars(:rating='product.attributes.rating')
        span.product-rating-count(v-text='reviewsCountText')
      .product-card-variants-count(v-if='variants.length > 1' v-text='variantCountText')
</template>

<script>
import { pushDataLayerEvent } from '@/modules/services/gtm'
import { mapState } from 'vuex'
import get from 'lodash/get'
import ProductPrice from './product-price.vue'
import ProductHeart from './product-heart.vue'
import ProductRatingStars from './product-rating-stars.vue'
import ProductCallToAction from './product-call-to-action.vue'
import ProductPromo from './product-promo.vue'
import VisibilityObserver from '@/modules/services/visibility-observer'

export default {
  components: {
    ProductPrice,
    ProductHeart,
    ProductRatingStars,
    ProductCallToAction,
    ProductPromo
  },

  props: {
    product: {
      type: Object,
      default: () => {}
    },
    variants: {
      type: Array,
      default: () => []
    },
    size: {
      type: String,
      default: 'lg'
    },
    lazy: {
      type: Boolean,
      default: false
    },
    selectedVariant: {
      type: Object,
      default: () => {}
    },
    gaOptions: {
      type: Object,
      default: () => {}
    },
    imageSize: {
      type: String,
      default: 'closeup-image-urls'
    },
    showHeart: {
      type: Boolean,
      default: true
    },
    earlyAccessBadge: {
      type: String,
      default: ''
    },
    showAtb: {
      type: Boolean,
      default: true
    },
    showRemindMe: {
      type: Boolean,
      default: false
    },
    isAppExclusive: {
      type: Boolean,
      default: false
    },
    earlyAccessNotificationEventDetails: {
      type: Object,
      default: () => {}
    }
  },

  data () {
    return {
      currentVariant: {},
      showProductActions: false,
      // Becomes true the first time user hovers on card and actions have been shown
      // Used to prevent initial render of actions while still preserving rendering performance
      hasShownProductActions: false,
      reviewsCountExpData: '',
      observer: this.requestFromRecommendedProductsCarousel() ? this.visibilityObserver() : null
    }
  },

  computed: {
    ...mapState({
      ads: state => state.product.ads
    }),

    label () {
      // TODO: to remove feature flag once launched
      if (vueSupport.showVariantTagsOnPlp) {
        return this.currentVariant.attributes.tags?.map(tag => tag.text)?.join(' | ')
      }

      if (this.product.attributes['new-arrival']) {
        return this.$t('common.new')
      }

      return ''
    },

    productsShowRoute () {
      if (this.variants.length > 1) {
        return {
          name: 'products-show-variant',
          params: {
            id: this.product.attributes['slug-url'],
            variantId: this.currentVariant.attributes['slug-url']
          }
        }
      } else {
        return {
          name: 'products-show',
          params: {
            id: this.product.attributes['slug-url']
          }
        }
      }
    },

    variantImage () {
      return this.currentVariant.attributes[this.imageSize][0] || this.product.attributes[this.imageSize][0] || ''
    },

    productOptionTypeText () {
      return this.product.attributes['option-type-categories'][0] || 'type'
    },

    variantCountText () {
      return `${this.variants.length} ${this.productOptionTypeText}s`
    },

    variantsOnCarousel () {
      if (this.size === 'sm') {
        return 7
      } else if (this.size === 'md') {
        return 9
      } else {
        return 11
      }
    },

    productAd () {
      return this.ads.find(ad => ad.id === get(this.product, 'relationships["featured-ad"].data.id', '-1'))
    },

    gaClasses () {
      return this.gaOptions ? this.gaOptions.classes : []
    },

    reviewsCountText () {
      const reviewsCount = this.product.attributes['reviews-count']

      return (reviewsCount && reviewsCount > 0) ? `(${this.product.attributes['reviews-count']})` : ''
    },

    outOfStock () {
      const inStock = !this.currentVariant.attributes['sold-out']
      const isComingSoon =
        this.earlyAccessBadge === this.$t('products.earlyAccess.comingSoon')

      return !(inStock || isComingSoon)
    },

    showRemindMeExtended () {
      if (this.outOfStock && this.isAppExclusive) {
        return false
      }
      return this.showRemindMe
    }
  },

  watch: {
    '$route' (to, from) {
      if (from.fullPath === '/cart') {
        window.location.href = to.fullPath
      }
    }
  },

  created () {
    if (this.selectedVariant) {
      this.currentVariant = this.selectedVariant
    } else {
      this.currentVariant = this.activeVariant(this.product)
    }
  },

  mounted () {
    if (this.observer) {
      this.observer.observe(this.$el)
    }
  },

  destroyed () {
    if (this.observer) {
      this.observer.unobserve(this.$el)
    }
  },

  methods: {
    mouseoverProductCard () {
      this.showProductActions = true
      this.hasShownProductActions = true
    },

    mouseleaveProductCard () {
      this.showProductActions = false
    },

    variantChange (variant) {
      this.currentVariant = variant
    },

    activeVariant (product) {
      let defaultVariant = this.variants.find(variant => parseInt(variant.id) === product.attributes['featured-variant-id'])
      defaultVariant = defaultVariant || this.variants[0]

      return defaultVariant
    },

    setProduct () {
      const product = get(this, '$store.state.product.product', {})
      if (product.id && this.product.id === product.id) {
        return
      }

      this.$store.commit('setProduct', this.product)
      this.$store.commit('setProductVariants', this.variants)
      this.$store.commit('setCurrentVariant', this.currentVariant)
      this.$store.commit('setAd', this.productAd)
      this.$store.commit('setVideos', [])
      this.$store.commit('setImageArticles', [])
      this.$store.commit('setReviewsCount', this.product.attributes['reviews-count'])
    },

    handleProductClick () {
      this.setProduct()

      if (this.requestFromRecommendedProductsCarousel()) {
        this.recordGtmEventForCarousel('product_click')
      }
    },

    requestFromRecommendedProductsCarousel () {
      return this.gaOptions?.targetContainer === 'recommended-products-carousel'
    },

    visibilityObserver () {
      const rootMargin = vueSupport.isMobile ? '-115px 0px -45px 0px' : '-75px 0px 0px 0px'
      const opts = {
        intersectionRatio: 1.0,
        rootMargin: rootMargin
      }

      return VisibilityObserver(() => this.recordGtmEventForCarousel('recommended_for_you_card_impression'), opts)
    },

    notifyDataLayer () {
      pushDataLayerEvent('product_click', {
        product_id: this.product.id,
        variant_id: this.currentVariant.id,
        product_tag: this.label,
        product_highlight: '',
        product_seal: this.currentVariant.attributes.seal,
        origin: document.referrer || '',
        status: this.currentVariant?.attributes['launch-status'],
        filter_type: this.earlyAccessBadge
      })
    },

    recordGtmEventForCarousel (eventName) {
      const baseParams = {
        ...this.gaOptions.params,
        product_id: this.product.id,
        variant_id: this.currentVariant.id,
        card_sequence_num: this.gaOptions.cardSequenceNumber
      }

      const params =
        eventName === 'product_click'
          ? {
            ...baseParams,
            status: this.currentVariant?.attributes['launch-status'],
            filter_type: this.earlyAccessBadge
          }
          : { ...baseParams, page_path: window.location.pathname }
      pushDataLayerEvent(eventName, { params })
    }
  }
}
</script>

<style scoped lang='scss'>
  .product-card .product-price {
    margin-bottom: 0;
  }
  .product-rating-count {
    margin-left: 4px;
  }
  .product-card-early-access {
    position: absolute;
    top: 10px;
    height: 20px;
    align-content: center;
    border-radius: 4px;
    padding-inline: 8px;
    background-color: #E74C6F;
    max-width: 100%;
    font-family: Sephora Sans;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
    color: white;
    text-overflow: ellipsis;
    z-index: 1;
  }
</style>
